import { types, flow, getSnapshot } from 'mobx-state-tree';
import { Meeting } from './Meeting';
import { ApiStore } from './ApiStore';

const _meetingStore = types.model('MeetingStore', {
    meetingTypeKey: types.maybe(types.string),
    meeting: types.maybe(Meeting),
    documentKey: types.optional(types.string, '')
})

export const MeetingStore = types.compose(_meetingStore, ApiStore)
    .actions(self => ({
        setMeetingType: flow(function* setMeetingType(meetingTypeKey, companyId) {
            self.meetingTypeKey = meetingTypeKey;

            // Clear Document Defense state so it doesn't leak into standard pitches
            self.documentKey = '';

            const url = `/murphy/meeting?meetingTypeKey=${meetingTypeKey}&companyId=${companyId}`;

            // Fetch meeting data from API with fallback for development
            let response;
            try {
                response = yield self.getApi(url);

                self.meetingTypeKey = meetingTypeKey;

                // Always create a fresh meeting object instead of updating existing one
                // This ensures no stale state persists between meeting type changes
                self.meeting = Meeting.create(response);

            } catch (error) {
                console.error('Murphy meeting construction failed!', error.message);
            }

            return self.meeting;
        }),
        getInteractionSnapshots() {
            // Safely get snapshots of current interactions
            if (!self.meeting || !self.meeting.interactions) {
                return [];
            }

            return self.meeting.interactions.map(interaction => {
                try {
                    return getSnapshot(interaction);
                } catch (error) {
                    // Fallback for detached models
                    return {
                        duration: interaction.duration,
                        text: interaction.text,
                        startTime: interaction.startTime,
                        participantName: interaction.participantName
                    };
                }
            });
        },
        generateAudio: flow(function* generateAudio(questionText, participantName) {
            try {
                return yield self.getApi(`/murphy/audio?text=${encodeURIComponent(questionText)}&participantName=${encodeURIComponent(participantName)}`);
            } catch (error) {
                console.warn('Murphy audio generation failed, using fallback for development:', error.message);
                // Return null so the component can handle gracefully
                return null;
            }
        }),
        // Initialize Q&A Practice session with questions from company Q&A library
        initQASession: flow(function* initQASession(companyId, questionCount = 5) {
            self.meetingTypeKey = 'qa_practice';

            // Clear Document Defense state — Objection Handling is a different flow
            self.documentKey = '';

            const url = `/murphy/qa-session?companyId=${companyId}&questionCount=${questionCount}`;

            try {
                const response = yield self.getApi(url);
                console.log('MeetingStore: Q&A session initialized', response);

                // Create meeting object with Q&A data
                // Filter participant fields to match the Participant model (MST rejects extra props)
                const participants = (response.participants || []).map(p => ({
                    name: p.name,
                    avatar: p.avatar || undefined,
                    avatarColor: p.avatarColor || undefined
                }));

                // Filter question fields to match the Question model
                const questions = (response.questions || []).map(q => ({
                    index: q.index,
                    rephrasedQuestion: q.rephrasedQuestion,
                    participantName: q.participantName,
                    relatedTo: q.relatedTo || null
                }));

                self.meeting = Meeting.create({
                    meetingTypeKey: 'qa_practice',
                    level: 3,
                    sessionId: response.sessionId,
                    questions: questions,
                    questionCount: response.questionCount,
                    participants: participants,
                    interactions: []
                });

                return self.meeting;
            } catch (error) {
                console.error('Q&A session initialization failed!', error.message);
                throw error;
            }
        }),
        // Initialize Document Defense session with questions generated from an uploaded document
        // Uses async pattern: GET to start generation, then poll for results
        initDocumentQASession: flow(function* initDocumentQASession(companyId, questionCount = 5, documentKey) {
            self.meetingTypeKey = 'qa_practice';
            self.documentKey = documentKey;

            try {
                // Step 1: Kick off async generation via GET with query params
                const qs = `companyId=${encodeURIComponent(companyId)}&questionCount=${questionCount}&documentKey=${encodeURIComponent(documentKey)}`;
                const initResponse = yield self.getApi(`/murphy/doc-qa-session?${qs}`);
                console.log('MeetingStore: Document Defense generation started', initResponse);
                const { sessionId } = initResponse;

                // Step 2: Poll for results (every 3s, up to 2 minutes)
                const maxAttempts = 40;
                let response = null;
                for (let attempt = 0; attempt < maxAttempts; attempt++) {
                    // Bail out if user navigated to a different session type
                    if (self.meetingTypeKey !== 'qa_practice') {
                        console.log('MeetingStore: Document Defense poll aborted — user switched session type');
                        return null;
                    }
                    yield new Promise(resolve => setTimeout(resolve, 3000));
                    const status = yield self.getApi(`/murphy/doc-qa-status?sessionId=${sessionId}`);
                    console.log(`MeetingStore: Poll attempt ${attempt + 1}/${maxAttempts}, status: ${status.status}`);

                    if (status.status === 'ready') {
                        response = status;
                        break;
                    }
                    if (status.status === 'error') {
                        const isOverloaded = status.error && (status.error.includes('529') || status.error.includes('overloaded') || status.error.includes('Overloaded'));
                        throw new Error(isOverloaded
                            ? 'Murphy is temporarily busy — please try again in a moment.'
                            : 'Failed to prepare questions from the document. Please try again.');
                    }
                }

                if (!response) {
                    throw new Error('Question generation timed out. Please try again.');
                }

                // Final guard: don't overwrite if user switched away during last poll
                if (self.meetingTypeKey !== 'qa_practice') {
                    console.log('MeetingStore: Document Defense ready but user switched session type — discarding');
                    return null;
                }

                console.log('MeetingStore: Document Defense session ready', response);

                const participants = (response.participants || []).map(p => ({
                    name: p.name,
                    avatar: p.avatar || undefined,
                    avatarColor: p.avatarColor || undefined
                }));

                const questions = (response.questions || []).map(q => ({
                    index: q.index,
                    rephrasedQuestion: q.rephrasedQuestion,
                    participantName: q.participantName,
                    relatedTo: q.relatedTo || null
                }));

                self.meeting = Meeting.create({
                    meetingTypeKey: 'qa_practice',
                    level: 3,
                    sessionId: response.sessionId,
                    questions: questions,
                    questionCount: response.questionCount,
                    participants: participants,
                    interactions: []
                });

                return self.meeting;
            } catch (error) {
                console.error('Document Defense session initialization failed!', error.message);
                throw error;
            }
        }),
        // Initialize Messaging Defense session — Murphy generates objections from company profile
        // Uses async pattern (same as Document Defense): GET to start, then poll for results
        initProspectPrepSession: flow(function* initProspectPrepSession(companyId, questionCount = 5) {
            self.meetingTypeKey = 'qa_practice';
            self.documentKey = '';

            try {
                const qs = `companyId=${encodeURIComponent(companyId)}&questionCount=${questionCount}`;
                const initResponse = yield self.getApi(`/murphy/prospect-prep?${qs}`);
                console.log('MeetingStore: Messaging Defense generation started', initResponse);
                const { sessionId } = initResponse;

                const maxAttempts = 40;
                let response = null;
                for (let attempt = 0; attempt < maxAttempts; attempt++) {
                    if (self.meetingTypeKey !== 'qa_practice') {
                        console.log('MeetingStore: Messaging Defense poll aborted — user switched session type');
                        return null;
                    }
                    yield new Promise(resolve => setTimeout(resolve, 3000));
                    const status = yield self.getApi(`/murphy/prospect-prep-status?sessionId=${sessionId}`);
                    console.log(`MeetingStore: Messaging Defense poll attempt ${attempt + 1}/${maxAttempts}, status: ${status.status}`);

                    if (status.status === 'ready') {
                        response = status;
                        break;
                    }
                    if (status.status === 'error') {
                        const isOverloaded = status.error && (status.error.includes('529') || status.error.includes('overloaded') || status.error.includes('Overloaded'));
                        throw new Error(isOverloaded
                            ? 'Murphy is temporarily busy — please try again in a moment.'
                            : 'Failed to generate prospect prep questions. Please try again.');
                    }
                }

                if (!response) {
                    throw new Error('Question generation timed out. Please try again.');
                }

                if (self.meetingTypeKey !== 'qa_practice') {
                    console.log('MeetingStore: Messaging Defense ready but user switched — discarding');
                    return null;
                }

                console.log('MeetingStore: Messaging Defense session ready', response);

                const participants = (response.participants || []).map(p => ({
                    name: p.name,
                    avatar: p.avatar || undefined,
                    avatarColor: p.avatarColor || undefined
                }));

                const questions = (response.questions || []).map(q => ({
                    index: q.index,
                    rephrasedQuestion: q.rephrasedQuestion,
                    participantName: q.participantName,
                    relatedTo: q.relatedTo || null
                }));

                self.meeting = Meeting.create({
                    meetingTypeKey: 'qa_practice',
                    level: 3,
                    sessionId: response.sessionId,
                    questions: questions,
                    questionCount: response.questionCount,
                    participants: participants,
                    interactions: []
                });

                return self.meeting;
            } catch (error) {
                console.error('Messaging Defense session initialization failed!', error.message);
                throw error;
            }
        }),
        clearDocumentSession() {
            self.documentKey = '';
        }
    }));